import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionInfo } from '@/hooks/useBilling';

export type BusinessType =
  | 'restaurant'
  | 'qsr'
  | 'bar'
  | 'cafe'
  | 'retail'
  | 'wholesale'
  | 'other';

export type PlanTier = 'Entry' | 'Core' | 'Pro' | 'Enterprise';

export type ModuleKey =
  | 'pos'
  | 'orders'
  | 'settleRegisters'
  | 'floorPlan'
  | 'kds'
  | 'kdsStations'
  | 'products'
  | 'inventory'
  | 'standardization'
  | 'promotions'
  | 'customers'
  | 'reports'
  | 'auditLogs'
  | 'commandCenter'
  | 'team'
  | 'utilities';

export type ModuleCategory =
  | 'OPERATIONS'
  | 'KITCHEN'
  | 'CATALOG'
  | 'CUSTOMERS'
  | 'INSIGHTS'
  | 'ADMIN';

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  description: string;
  category: ModuleCategory;
  minPlan?: PlanTier;
}

export interface ModuleState {
  key: ModuleKey;
  enabled: boolean; // effective (after plan gating)
  enabledByOrg: boolean; // after business defaults + org overrides
  available: boolean; // subscription allows
  minPlan?: PlanTier;
  lockedReason?: string;
  label: string;
  description: string;
  category: ModuleCategory;
}

type ModuleSettingsJson = Partial<Record<ModuleKey, { enabled?: boolean }>>;

const PLAN_ORDER: PlanTier[] = ['Entry', 'Core', 'Pro', 'Enterprise'];

function normalizePlan(planName: string | null | undefined): PlanTier {
  const raw = (planName || 'Entry').toString().trim().toLowerCase();

  if (raw.includes('enterprise')) return 'Enterprise';
  if (raw.includes('pro')) return 'Pro';
  if (raw.includes('core')) return 'Core';
  if (raw.includes('entry') || raw.includes('starter')) return 'Entry';

  // If unknown, avoid blocking features unexpectedly
  return 'Pro';
}

function planMeetsMinPlan(plan: PlanTier, minPlan: PlanTier): boolean {
  return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(minPlan);
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    key: 'pos',
    label: 'POS',
    description: 'Point of Sale interface',
    category: 'OPERATIONS',
  },
  {
    key: 'orders',
    label: 'Orders',
    description: 'Order history and management',
    category: 'OPERATIONS',
  },
  {
    key: 'settleRegisters',
    label: 'Settle Registers',
    description: 'Register reconciliation and settlements',
    category: 'OPERATIONS',
  },
  {
    key: 'floorPlan',
    label: 'Floor Plan',
    description: 'Area management and layout',
    category: 'OPERATIONS',
    minPlan: 'Core',
  },
  {
    key: 'kds',
    label: 'Preparation Display',
    description: 'Order preparation and fulfillment display',
    category: 'KITCHEN',
  },
  {
    key: 'kdsStations',
    label: 'Work Stations',
    description: 'Order routing and station configuration',
    category: 'KITCHEN',
    minPlan: 'Core',
  },
  {
    key: 'products',
    label: 'Products',
    description: 'Product catalog management',
    category: 'CATALOG',
  },
  {
    key: 'inventory',
    label: 'Inventory',
    description: 'Stock control and inventory management',
    category: 'CATALOG',
  },
  {
    key: 'standardization',
    label: 'Standardization',
    description: 'Recipes and standardized templates',
    category: 'CATALOG',
    minPlan: 'Core',
  },
  {
    key: 'promotions',
    label: 'Promotions',
    description: 'Promotions and discount campaigns',
    category: 'CATALOG',
  },
  {
    key: 'customers',
    label: 'Customers',
    description: 'Customer profiles and accounts',
    category: 'CUSTOMERS',
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'Sales and operational reporting',
    category: 'INSIGHTS',
  },
  {
    key: 'auditLogs',
    label: 'Audit Logs',
    description: 'Activity logs and security auditing',
    category: 'INSIGHTS',
    minPlan: 'Core',
  },
  {
    key: 'commandCenter',
    label: 'Command Center',
    description: 'Back office configuration',
    category: 'ADMIN',
  },
  {
    key: 'team',
    label: 'Team',
    description: 'Staff management (superadmin only)',
    category: 'ADMIN',
  },
  {
    key: 'utilities',
    label: 'Utilities',
    description: 'System utilities (superadmin only)',
    category: 'ADMIN',
  },
];

const MODULES_BY_KEY = MODULE_DEFINITIONS.reduce(
  (acc, def) => {
    acc[def.key] = def;
    return acc;
  },
  {} as Record<ModuleKey, ModuleDefinition>
);

const BASE_DEFAULTS: Record<ModuleKey, boolean> = {
  pos: true,
  orders: true,
  settleRegisters: true,
  floorPlan: false,
  kds: false,
  kdsStations: false,
  products: true,
  inventory: true,
  standardization: false,
  promotions: true,
  customers: true,
  reports: true,
  auditLogs: true,
  commandCenter: true,
  team: true,
  utilities: true,
};

const BUSINESS_DEFAULT_OVERRIDES: Record<BusinessType, Partial<Record<ModuleKey, boolean>>> = {
  restaurant: {
    floorPlan: true,
    kds: true,
    kdsStations: true,
    standardization: true,
  },
  qsr: {
    floorPlan: false,
    kds: true,
    kdsStations: true,
    standardization: true,
  },
  bar: {
    floorPlan: true,
    kds: true,
    kdsStations: true,
    standardization: true,
  },
  cafe: {
    floorPlan: true,
    kds: true,
    kdsStations: true,
    standardization: true,
  },
  retail: {
    floorPlan: false,
    kds: false,
    kdsStations: false,
    standardization: false,
  },
  wholesale: {
    floorPlan: false,
    kds: false,
    kdsStations: false,
    standardization: false,
  },
  other: {
    floorPlan: false,
    kds: false,
    kdsStations: false,
    standardization: false,
  },
};

function buildBusinessDefaults(businessType: BusinessType): Record<ModuleKey, boolean> {
  return {
    ...BASE_DEFAULTS,
    ...(BUSINESS_DEFAULT_OVERRIDES[businessType] || {}),
  };
}

export function useModuleSettings() {
  const queryClient = useQueryClient();
  const { organization, updateOrganization, profile } = useAuth();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscriptionInfo();
  const [savingModuleKey, setSavingModuleKey] = useState<ModuleKey | null>(null);
  const [isResettingModules, setIsResettingModules] = useState(false);
  const [savingBusinessType, setSavingBusinessType] = useState(false);

  const isSuperadmin = profile?.role === 'superadmin';

  const businessType: BusinessType = useMemo(() => {
    const raw = (organization as any)?.business_type as BusinessType | null | undefined;
    return raw || 'restaurant';
  }, [organization]);

  const planTier = useMemo(() => normalizePlan(subscription?.plan_name), [subscription?.plan_name]);

  const orgModuleSettings: ModuleSettingsJson = useMemo(() => {
    const settings = (organization as any)?.settings as { modules?: ModuleSettingsJson } | null | undefined;
    return settings?.modules || {};
  }, [organization]);

  const businessDefaults = useMemo(() => buildBusinessDefaults(businessType), [businessType]);

  const modules = useMemo(() => {
    const result: Record<ModuleKey, ModuleState> = {} as any;

    for (const def of MODULE_DEFINITIONS) {
      const defaultEnabled = businessDefaults[def.key];
      const overrideEnabled = orgModuleSettings?.[def.key]?.enabled;
      const enabledByOrg = typeof overrideEnabled === 'boolean' ? overrideEnabled : defaultEnabled;

      const available = def.minPlan ? planMeetsMinPlan(planTier, def.minPlan) : true;
      // Plan gating is enforced for non-superadmin users.
      // Superadmin can override for testing even if out of plan (UI shows a warning).
      const enabled = available ? enabledByOrg : (isSuperadmin ? enabledByOrg : false);

      result[def.key] = {
        key: def.key,
        enabled,
        enabledByOrg,
        available,
        minPlan: def.minPlan,
        lockedReason: !available && def.minPlan
          ? `Requires ${def.minPlan} plan`
          : undefined,
        label: def.label,
        description: def.description,
        category: def.category,
      };
    }

    return result;
  }, [businessDefaults, orgModuleSettings, planTier]);

  const isModuleEnabled = useCallback((key: ModuleKey): boolean => {
    // If there is no org context (e.g. global superadmin), don't block navigation
    if (!organization?.id) return true;
    return modules[key]?.enabled ?? true;
  }, [organization?.id, modules]);

  const canToggleModules = useMemo(() => {
    // Match existing org-wide settings behavior (Billing/Appearance)
    return profile?.role === 'superadmin' || profile?.role === 'primary_admin';
  }, [profile?.role]);

  const setModuleEnabled = useCallback(async (key: ModuleKey, enabled: boolean) => {
    if (!organization?.id) return;
    if (!canToggleModules) return;

    // Subscription gating is not finalized. For now:
    // - superadmin can enable anything (with UI warning)
    // - primary_admin cannot enable modules above plan, but can disable them
    const def = MODULES_BY_KEY[key];
    if (!isSuperadmin && enabled && def?.minPlan && !planMeetsMinPlan(planTier, def.minPlan)) {
      return;
    }

    setSavingModuleKey(key);
    try {
      const currentSettings = ((organization as any)?.settings || {}) as Record<string, unknown>;
      const currentModules = (currentSettings.modules || {}) as ModuleSettingsJson;

      const newModules: ModuleSettingsJson = {
        ...currentModules,
        [key]: {
          ...(currentModules[key] || {}),
          enabled,
        },
      };

      const newSettings = {
        ...currentSettings,
        modules: newModules,
      };

      await updateOrganization({ settings: newSettings });
      queryClient.invalidateQueries({ queryKey: ['org-settings', organization.id] });
    } catch (err) {
      console.error('[useModuleSettings] Failed to update module setting:', err);
    } finally {
      setSavingModuleKey(null);
    }
  }, [organization, canToggleModules, isSuperadmin, planTier, updateOrganization, queryClient]);

  const resetModulesToBusinessDefaults = useCallback(async () => {
    if (!organization?.id) return;
    if (!canToggleModules) return;

    setIsResettingModules(true);
    try {
      const currentSettings = ((organization as any)?.settings || {}) as Record<string, unknown>;
      const newModules: ModuleSettingsJson = {};

      for (const def of MODULE_DEFINITIONS) {
        const available = def.minPlan ? planMeetsMinPlan(planTier, def.minPlan) : true;
        const recommended = businessDefaults[def.key];
        // If not superadmin, keep plan gating when resetting defaults.
        // Superadmin may reset to recommended regardless of plan (warned in UI).
        newModules[def.key] = { enabled: isSuperadmin ? recommended : (available ? recommended : false) };
      }

      const newSettings = {
        ...currentSettings,
        modules: newModules,
      };

      await updateOrganization({ settings: newSettings });
      queryClient.invalidateQueries({ queryKey: ['org-settings', organization.id] });
    } catch (err) {
      console.error('[useModuleSettings] Failed to reset modules to defaults:', err);
    } finally {
      setIsResettingModules(false);
    }
  }, [organization, canToggleModules, isSuperadmin, businessDefaults, planTier, updateOrganization, queryClient]);

  const setBusinessType = useCallback(async (next: BusinessType) => {
    if (!organization?.id) return;
    if (!canToggleModules) return;

    setSavingBusinessType(true);
    try {
      await updateOrganization({ business_type: next } as any);
      queryClient.invalidateQueries({ queryKey: ['organization-subdomain'] });
    } catch (err) {
      console.error('[useModuleSettings] Failed to update business type:', err);
    } finally {
      setSavingBusinessType(false);
    }
  }, [organization, canToggleModules, updateOrganization, queryClient]);

  return {
    businessType,
    setBusinessType,
    planTier,
    subscriptionLoading,
    canToggleModules,
    isSuperadmin,
    modules,
    isModuleEnabled,
    setModuleEnabled,
    resetModulesToBusinessDefaults,
    savingModuleKey,
    isResettingModules,
    savingBusinessType,
  };
}

